import { BatteryFull, BatteryWarning, BellOff, PlugZap } from 'lucide-react';

import ArrowTooltip from '@/components/custom-ui/customTooltip';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import { formatNumberWithCommas } from '@/utils/FormatNumber';

// Pre-define static styles to improve rendering performance
const STATUS_STYLES = {
  container: 'flex h-16 w-full items-center justify-center',
  card: 'flex flex-col h-14 w-full items-center justify-center rounded-md border px-1.5 py-1 font-roboto text-xs font-bold shadow-sm',
  icon: 'mb-1 h-5 w-5',
  value: 'font-medium text-sm',
};

type Props = {
  item: IOneItemInPrepareForRequest;
};

function RetailDiscountStatusCol({ item }: Props) {
  const diff = Math.abs(item?.RetailDiff || 0);
  const status: 'needStock' | 'canTransfer' | 'noTrans' | 'overStock' =
    item.RetailStatus;

  // Map each status type to its visual properties
  const typeMapping: Record<
    string,
    {
      label: string;
      bg: string;
      text: string;
      icon: React.ReactNode;
      border: string;
    }
  > = {
    needStock: {
      label: 'يحتاج رصيد',
      bg: 'bg-gradient-to-br from-red-100/80 via-red-200/50 to-red-100/80 dark:from-red-900/30 dark:via-red-800/20 dark:to-red-900/30',
      text: 'text-red-700 dark:text-red-300',
      icon: <BatteryWarning className={STATUS_STYLES.icon} />,
      border: 'border-red-200/50 dark:border-red-800/30',
    },
    canTransfer: {
      label: 'التحويل من مخزن اخر',
      bg: 'bg-gradient-to-br from-amber-100/80 via-amber-200/50 to-amber-100/80 dark:from-amber-900/30 dark:via-amber-800/20 dark:to-amber-900/30',
      text: 'text-amber-700 dark:text-amber-300',
      icon: <PlugZap className={STATUS_STYLES.icon} />,
      border: 'border-amber-200/50 dark:border-amber-800/30',
    },
    noTrans: {
      label: 'لا يوجد حركة',
      bg: 'bg-gradient-to-br from-gray-100/80 via-gray-200/50 to-gray-100/80 dark:from-gray-900/30 dark:via-gray-800/20 dark:to-gray-900/30',
      text: 'text-gray-700 dark:text-gray-300',
      icon: <BellOff className={STATUS_STYLES.icon} />,
      border: 'border-gray-200/50 dark:border-gray-800/30',
    },
    overStock: {
      label: 'رصيد زيادة',
      bg: 'bg-gradient-to-br from-green-100/80 via-green-200/50 to-green-100/80 dark:from-green-900/30 dark:via-green-800/20 dark:to-green-900/30',
      text: 'text-green-700 dark:text-green-300',
      icon: <BatteryFull className={STATUS_STYLES.icon} />,
      border: 'border-green-200/50 dark:border-green-800/30',
    },
  };

  // Get the style configuration for the current status
  const currentStyle = typeMapping[status] || typeMapping.noTrans;

  return (
    <div className={STATUS_STYLES.container}>
      <ArrowTooltip color="primary" msg={currentStyle.label}>
        <div
          className={`${STATUS_STYLES.card} ${currentStyle.bg} ${currentStyle.text} ${currentStyle.border}`}
        >
          <div className="flex items-center justify-center">
            {currentStyle.icon}
          </div>
          <span className={STATUS_STYLES.value}>
            {formatNumberWithCommas(diff)}
          </span>
        </div>
      </ArrowTooltip>
    </div>
  );
}

export default RetailDiscountStatusCol;
